// DeviceID -> human label + which control screen to use. Mirrors the Flutter
// screenRedirector() mapping (docs/03-devices.md).

import { channelsFor, fanChannelsFor, masterChannelsFor } from './statusFormat';
import { panelColumns } from './buttonLayout';
import { isCurtainDevice, curtainNoun } from './curtain';
import { deviceCatalogEntry as catalogEntry } from './deviceCatalog';

export const ONE_TOUCH_IDS = [5, 6, 21, 22, 28, 31, 33, 40, 42, 55, 59, 60, 80];

// Combined switch + fan panels: DeviceID 92 = "4 + 1" (4 switches + 1 fan),
// DeviceID 93 = "7 + 1". Flutter routes both to switchFanScreen with a different
// switchCount — SWITCH_FAN_COUNT is that count.
export const SWITCH_FAN_COUNT = { 92: 4, 93: 7 };

export function deviceTypeKey(deviceId) {
  const id = Number(deviceId);
  if (id === 49) return 'fourTouch';
  if (id === 56) return 'twoTouch';
  if (id === 53) return 'fan';
  /*
   * Curtains and the sliding gate — one key, because they are one control.
   *
   * ⚠️ 13 AND 37 USED TO FALL THROUGH TO THE GENERIC OUTPUT UI, which gave a
   * curtain two independent relay toggles labelled "Curtain Open" / "Curtain
   * Close". That is the wrong shape twice over: the relays are momentary, so a
   * toggle has no OFF to correspond to, and the pair has no interlock — the
   * firmware admits as much (`OtaForTuya.ino:4758`), so both directions could be
   * energised from the screen. 7 Curtain Controllers were deployed like that.
   *
   * The gate is included on the firmware's own say-so: DeviceType 37 accepts the
   * curtain words *"because the app presents the gate with the same UI as a
   * curtain"* (`OtaForTuya.ino:6039`). See core/devices/curtain.js — including
   * why 78 and 79 stay out.
   */
  if (isCurtainDevice(id)) return 'curtain';
  if (id === 24) return 'rgb';
  if (id === 25) return 'wwh';
  if (id === 23) return 'wwrgb';
  if (id === 3) return 'door';
  if (id === 82) return 'singlePhase';
  if (id === 16) return 'threePhase';
  if (id === 92 || id === 93) return 'switchFan';
  /*
   * IR blasters — keypads, not switches. Zero outputs, so before this they fell
   * to the generic card and told the owner of a working blaster
   * "No controllable outputs found".
   *
   * ⚠️ 19 ONLY IS PROVEN ON HARDWARE. Flutter's gate was `DeviceID in
   * (19,57,58,62)` (repository.dart:1257-1259) and copying that set wholesale is
   * the trap:
   *   • 58 "Ac On Off" has **Outputs 1** — it is a RELAY with a real switch UI,
   *     and giving it this key would take that away. Excluded.
   *   • 57 and 62 share the shape (0 outputs, On_off 1) but run a different
   *     firmware image — 19 is OTABinFileId 7 (OTA_AIRCONDITIONER_IR.ino.bin)
   *     while 57/62 point at 1 — and there is not one 57/58/62 row in the live
   *     DevicesInstalled to test against. Routed here because a remote is a far
   *     better guess than "no outputs", but the vocabulary is unconfirmed.
   */
  if (id === 19 || id === 57 || id === 62) return 'irBlaster';
  if (ONE_TOUCH_IDS.includes(id)) return 'oneTouch';

  /*
   * ── Everything else, from the backend's own catalogue ────────────────────
   *
   * Reached only after every hand-written case above has declined, so this can
   * never take a product away from a screen built for it — the explicit list
   * wins by construction, and stays right when the catalogue has not loaded.
   *
   * What it adds is every switch bank nobody has written a branch for. On the
   * live table that is not a handful: 0 (6+1), 1 (4+1), 10 (Tv Stand), 11
   * (5+1), 17 (5+2), 44, 68 (a bare BLDC fan), 87 (7+2), 91, and the eight new
   * glass panels 92–99. Ten of those were drawing generic output toggles.
   *
   * ⚠️ SENSORS CANNOT REACH HERE ANYWAY, and that is not luck: all three
   * routers — mobile DeviceScreen, desktop DeviceControls, desktop
   * DeviceControlCard — test `isSensorDevice(device)` BEFORE asking for a key.
   * The `Switch` test below is the second line of that defence, not the first.
   */
  const e = catalogEntry(id);
  if (e && e.type === 'Switch' && e.consistent) {
    // A fan and no switches is a fan, not a one-tile panel with a dial bolted
    // to it — DeviceID 68 "Bldc Fan Switch" is On_off 0, Dimming 1.
    if (e.onOff === 0 && e.dimming > 0) return 'fan';
    if (e.dimming > 0 && e.onOff > 0) return 'switchFan';
    // Reuse the purpose-built keys where the width matches one, so a catalogue
    // 4-gang is drawn by exactly the same screen as DeviceID 49.
    if (e.onOff === 1) return 'oneTouch';
    if (e.onOff === 2) return 'twoTouch';
    if (e.onOff === 4) return 'fourTouch';
    if (e.onOff > 0) return 'touch';
  }
  return 'unsupported';
}

/**
 * How many plain on/off relays a device exposes — NOT counting fan channels.
 *
 * Drives the tile grid and how much of the status frame is read as booleans.
 */
export function relayCount(deviceId) {
  const id = Number(deviceId);
  const e = catalogEntry(id);
  switch (deviceTypeKey(id)) {
    case 'fourTouch': return 4;
    case 'twoTouch': return 2;
    case 'oneTouch': return 1;
    // ⚠️ The catalogue FIRST here, unlike the routing above: SWITCH_FAN_COUNT
    // knows only 92 and 93, and its `|| 4` default would draw a 5+1 as a 4+1
    // and quietly lose a load. The table stays as the offline fallback.
    case 'switchFan': return (e && e.onOff) || SWITCH_FAN_COUNT[id] || 4;
    case 'touch': return (e && e.onOff) || 0;
    default: return 0;
  }
}

/** Fan channels on a switch panel — the trailing values, carrying speeds. */
export function fanCount(deviceId) {
  const k = deviceTypeKey(deviceId);
  if (k !== 'switchFan' && k !== 'touch') return 0;
  return fanChannelsFor(deviceId) || 0;
}

/** Does this panel's glass carry a master touch position? */
export function hasMasterButton(deviceId) {
  const k = deviceTypeKey(deviceId);
  if (k !== 'switchFan' && k !== 'touch' && k !== 'fourTouch'
    && k !== 'twoTouch' && k !== 'oneTouch') return false;
  return masterChannelsFor(deviceId) > 0;
}

// Grid column span per device. The 5-channel WWRGB light is wider so all its
// sliders fit; everything else uses the dense default.
/**
 * How wide a device's card needs to be.
 *
 * ⚠️ A PANEL IS AS WIDE AS ITS CHANNEL COUNT MAKES IT. This used to key on the
 * TYPE alone, so a 7+1 — eight channels, four panel columns — was given the
 * same quarter-row as a one-gang switch. Four labelled buttons cannot fit
 * ~55 px each, so they overflowed their grid tracks and drew on top of one
 * another: the overlap in the Bag room.
 *
 * The panel is two rows tall (buttonLayout.PANEL_ROWS), so the columns — not
 * the channels — are what set the width: 4 channels is 2 columns, 6 is 3, 8 is
 * 4. Growing with the columns keeps every button at a usable size instead of
 * compressing past the point of being readable.
 */
export function deviceGridSpan(deviceId) {
  const k = deviceTypeKey(deviceId);
  if (k === 'wwrgb') return { xs: 12, sm: 6, md: 6, lg: 4 };
  if (k === 'singlePhase' || k === 'threePhase') return { xs: 12, sm: 6, md: 6, lg: 4 };
  // A remote card carries a handful of shortcut keys plus a link to the pads —
  // the full 33-key TV pad belongs on the device screen, not in a room grid.
  if (k === 'irBlaster') return { xs: 12, sm: 6, md: 4, lg: 3 };
  const cols = panelColumns(channelsFor(deviceId) || 1);
  if (cols >= 4) return { xs: 12, sm: 8, md: 6, lg: 4 };
  if (cols === 3) return { xs: 12, sm: 6, md: 4, lg: 3 };
  return { xs: 6, sm: 4, md: 3, lg: 2 };
}

/**
 * Fixed card width in px, for the horizontal strips that cannot use a grid.
 *
 * Same rule as deviceGridSpan and the same reason — a strip that hands every
 * card 230 px squeezes an eight-channel panel into four 50 px columns.
 */
export function deviceCardWidth(deviceId) {
  const k = deviceTypeKey(deviceId);
  if (k === 'singlePhase' || k === 'threePhase' || k === 'wwrgb') return 380;
  const cols = panelColumns(channelsFor(deviceId) || 1);
  // ~78 px a column plus the card's own padding: enough for the power icon and
  // a few characters of name without the label being pure ellipsis.
  return Math.max(230, 78 * cols + 40);
}

export function deviceLabel(deviceId) {
  switch (deviceTypeKey(deviceId)) {
    // "Curtain" or "Sliding Gate" — same control, and a driveway gate labelled
    // "Curtain" is how an operator ends up doubting they are on the right row.
    case 'curtain': return curtainNoun(deviceId) === 'Gate' ? 'Sliding Gate' : 'Curtain';
    case 'fourTouch': return '4-Gang Switch';
    case 'twoTouch': return '2-Gang Switch';
    case 'oneTouch': return 'Switch';
    case 'fan': return 'Fan';
    case 'rgb': return 'RGB Light';
    case 'wwh': return 'White Light';
    case 'wwrgb': return 'WWRGB Light';
    case 'door': return 'Door Lock';
    case 'irBlaster': return 'IR Remote';
    case 'singlePhase': return 'Energy Meter';
    case 'threePhase': return '3-Phase Meter';
    // "7 + 1", "5 + 2" — both numbers from the catalogue, because the fan count
    // is not always one and a 5+2 labelled "5 + 1" is a load nobody looks for.
    case 'switchFan': return `${relayCount(deviceId)} + ${fanChannelsFor(deviceId) || 1} Switch/Fan`;
    case 'touch': return `${relayCount(deviceId)}-Gang Switch`;
    default: return 'Device';
  }
}
